import {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import removeRichTextDefault from './utils/removeRichText';

const labelsDefaults = {
	required: 'Required',
	maxLength: 'Text cannot exceed {number} characters.'
};

const useForm = ({
	initialValues = {},
	schema,
	onSubmit = null,
	onChange = null,
	labels = labelsDefaults,
	removeRichText = removeRichTextDefault,
	updateInitialValues = false
}) => {
	const [values, setValues] = useState(initialValues);
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (initialValues && updateInitialValues) setValues({...initialValues});
	}, []);

	const handleChange = (id, val, other = {}) => {
		let newValues = {...values, ...other, [id]: val};
		setValues(newValues);
		if (onChange) onChange(newValues);
		handleClearValidationError(id);
	};

	const isValueValid = (id) => {
		const isString = typeof values[id] === 'string';
		const isArray = Array.isArray(values[id]);
		const validObj = {valid: true, message: ''};
		const returnValidObj = (condition, message) => (condition ? validObj : {valid: false, message});
		const idSeparator = '::';
		let schemaObj = schema[id];
		if (schema[id] === undefined) {
			const prefix = id.split(idSeparator)[0];
			if (schema[prefix] !== undefined) schemaObj = schema[prefix];
		}

		const validateRequired = (valid) => {
			let message = labels.required;
			if (schemaObj?.requiredMessage) message = schemaObj?.requiredMessage;
			if (valid.valid) {
				const required = typeof schemaObj?.required === 'function' ? schemaObj?.required(values) : schemaObj?.required;

				if (required) {
					if (isString) {
						let val = values[id];
						if (schemaObj.excludeRichText) {
							val = removeRichText(val);
						}
						return returnValidObj(val.trim() !== '', message);
					}
					if (isArray) {
						const condition = schemaObj?.requiredCondition
							? schemaObj?.requiredCondition(values[id])
							: values[id].length > 0;
						return returnValidObj(condition, message);
					}
					return returnValidObj(values[id] !== null, message);
				}
			}

			return valid;
		};

		const validateMaxLength = (valid) => {
			let message = labels?.maxLength?.replace('{number}', schemaObj.maxLength);
			if (schemaObj?.maxLengthMessage) message = schemaObj?.maxLengthMessage?.replace('{number}', schemaObj.maxLength);
			if (valid.valid) {
				if (schemaObj.maxLength !== undefined) {
					if (isString) {
						let innerText = values[id];
						if (schemaObj.excludeRichText) {
							innerText = removeRichText(innerText);
						}
						return returnValidObj(innerText.length <= schemaObj.maxLength, message);
					}
				}
			}

			return valid;
		};

		let valid = {...validObj};
		// order matters here, required has to be the last one
		valid = validateMaxLength(valid);
		valid = validateRequired(valid);

		return valid;
	};

	const isFormValid = () => {
		let valid = true;
		Object.keys(values).forEach((key) => {
			if (!isValueValid(key).valid) valid = false;
		});
		return valid;
	};

	const handleValidateFields = () => {
		const newErrors = {};
		Object.keys(values).forEach((key) => {
			const {valid, message} = isValueValid(key);
			if (!valid) {
				newErrors[key] = {error: true, message};
			}
		});
		setErrors(newErrors);
	};

	const handleClearValidationError = (id) => {
		const newErrors = {...errors};
		delete newErrors[id];
		setErrors(newErrors);
	};

	const handleReset = () => {
		setValues(initialValues);
		setErrors({});
	};

	const handleSubmit = (callback) => {
		if (isFormValid()) {
			if (onSubmit) onSubmit(values, callback);
		} else {
			handleValidateFields();
		}
	};

	return {values, errors, handleChange, handleSubmit, handleReset, isFormValid, isValueValid};
};

const labelsShape = {
	required: PropTypes.string,
	maxLength: PropTypes.string
};

useForm.propTypes = {
	initialValues: PropTypes.shape({}),
	schema: PropTypes.shape({
		required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
		requiredMessage: PropTypes.string,
		maxLength: PropTypes.number,
		maxLengthMessage: PropTypes.string,
		excludeRichText: PropTypes.bool
	}),
	onSubmit: PropTypes.func,
	onChange: PropTypes.func,
	labels: PropTypes.shape(labelsShape),
	removeRichText: PropTypes.func,
	updateInitialValues: PropTypes.bool
};

export default useForm;
